/**
 * Axios Instance Configuration for REST API
 */
import axios from 'axios';
import { toast } from 'vue-sonner';

const api = axios.create({
    baseURL: '/',
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'  // SPA 요청 표시
    },
    withCredentials: true,
    timeout: 15000,  // 15초 타임아웃
});

// Router와 Store 참조 (main.js에서 초기화 후 설정)
let authStoreRef = null;
let routerRef = null;
let i18nRef = null;
// Antiforgery 재발급 single-flight promise (동시 실패 stampede 방지)
let antiforgeryRefreshPromise = null;

/**
 * main.js에서 앱 초기화 후 호출
 */
export function setAuthStore(store) {
    authStoreRef = store;
}

export function setRouter(router) {
    routerRef = router;
}

export function setI18n(i18n) {
    i18nRef = i18n;
}

/**
 * i18n 메시지 가져오기 (fallback 포함)
 * Why: vue-i18n의 t()는 키가 없으면 키 문자열 자체를 반환하므로,
 * 누락된 키일 때 fallback을 노출하려면 te()로 존재 여부를 먼저 확인해야 한다.
 */
function t(key, fallback) {
    const global = i18nRef?.global;
    if (global?.te?.(key)) {
        return global.t(key);
    }
    return fallback;
}

/**
 * 다음 시도까지 기다려야 하는 초를 꺼낸다. (2026-10-26)
 * 본문의 retryAfterSeconds 를 먼저 보고, 없으면 Retry-After 헤더를 본다 —
 * 미들웨어가 만드는 429 는 본문을 직접 쓰므로 둘 다 챙겨야 한다.
 * (Retry-After 는 HTTP 날짜 형식도 허용되지만 서버가 초만 보내므로 숫자만 읽는다)
 */
function readRetryAfter(error) {
    const fromBody = error?.response?.data?.retryAfterSeconds;
    if (Number.isFinite(fromBody) && fromBody > 0) return fromBody;

    const header = error?.response?.headers?.['retry-after'];
    const parsed = Number.parseInt(header, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// Request interceptor (CSRF 토큰 자동 추가)
api.interceptors.request.use(
    (config) => {
        console.log(`[API] Request: ${config.method?.toUpperCase()} ${config.url}`);

        // 앱에서 선택한 언어를 서버에 전달 → 이메일 발송이 브라우저 Accept-Language 대신
        // 사용자가 고른 앱 언어를 따르도록 (서버 EmailLanguageHelper 가 우선 적용).
        const appLang = i18nRef?.global?.locale?.value ?? i18nRef?.global?.locale;
        if (appLang) {
            config.headers['X-App-Language'] = appLang;
        }

        // GET 요청은 토큰 불필요
        if (config.method === 'get') {
            return config;
        }

        // Auth Store에서 Antiforgery 토큰 가져오기
        const token = authStoreRef?.antiforgeryToken;

        if (token) {
            config.headers['RequestVerificationToken'] = token;
        } else {
            console.warn('[API] No antiforgery token available');
        }

        return config;
    },
    (error) => {
        console.error('[API] Request error:', error?.message || 'unknown');
        return Promise.reject(error);
    }
);

// Response interceptor (리다이렉트 및 에러 처리)
api.interceptors.response.use(
    (response) => {
        console.log(`[API] Response: ${response.status} ${response.config.url}`);
        const data = response.data;

        // redirectUrl이 있으면 로그만 출력 (자동 리다이렉트 비활성화)
        // 각 컴포넌트에서 직접 처리하도록 변경 (IdentityServer 엔드포인트 처리를 위해)
        if (data && data.redirectUrl) {
            console.log(`[API] Redirect to: ${data.redirectUrl}`);
            // 자동 리다이렉트 제거 - 컴포넌트에서 직접 처리
            // IdentityServer 엔드포인트(/connect/*, /.well-known/*)는
            // window.location.href로 처리해야 하므로 각 컴포넌트에서 분기 처리
        }

        return response;
    },
    async (error) => {
        // ── Antiforgery 실패 자동 복구 (구조적 재발 방지, 2026-07-29) ──
        // 배경: 서버가 RefreshSignInAsync 로 인증 쿠키를 재발급하면 SPA 가 들고 있던
        // antiforgery 토큰이 무효화됨. 이후 non-GET 요청이 400 (빈 body) 로 실패.
        // (/manage 저장 흐름에서 '부서 삭제 실패 ×3' 사고, d9bb356f 에서 순서로 1차 수정)
        // 여기서는 전역 방어: 400 + body 에 detail 없음 + non-GET + 미재시도 요청이면
        // antiforgery 토큰 재발급 후 원 요청을 1회 재시도.
        {
            const cfg = error.config;
            const status = error.response?.status;
            const isNonGet = cfg && cfg.method && cfg.method.toLowerCase() !== 'get';
            // 서버 필터(AntiforgeryFailedHeaderFilter)가 antiforgery 실패 400 에만 다는 식별 헤더.
            // (2026-08-02 필터 실행 순서 수정으로 모든 antiforgery 400 에 확실히 부착됨.)
            // 헤더 전용으로 판정 — 이전의 빈-body 휴리스틱은 non-idempotent POST 를 오분류 재시도할
            // 위험이 있어 제거했다(헤더가 신뢰 가능해졌으므로 불필요).
            const headerFlag = error.response?.headers?.['x-antiforgery-failed'] === '1';
            const looksLikeAntiforgeryFail = status === 400 && isNonGet && headerFlag;
            if (looksLikeAntiforgeryFail && !cfg._antiforgeryRetried && authStoreRef?.refreshToken) {
                cfg._antiforgeryRetried = true;
                try {
                    // Single-flight: 동시 다발 실패 시 토큰 재발급은 1회만 (thundering herd 방지)
                    if (!antiforgeryRefreshPromise) {
                        antiforgeryRefreshPromise = authStoreRef.refreshToken()
                            .finally(() => { antiforgeryRefreshPromise = null; });
                    }
                    await antiforgeryRefreshPromise;
                    const newToken = authStoreRef.antiforgeryToken;
                    if (newToken) {
                        cfg.headers = cfg.headers || {};
                        cfg.headers['RequestVerificationToken'] = newToken;
                        console.warn(`[API] Antiforgery 400 → 토큰 재발급 후 재시도: ${cfg.method?.toUpperCase()} ${cfg.url}`);
                        return api.request(cfg);
                    }
                } catch (e) {
                    console.warn('[API] Antiforgery 재시도 준비 실패 (원 오류로 진행)');
                }
            }
        }
        // PII 방지: response body 원본 대신 status/url/code 만 (상세는 DEV 도구 Network 탭에서)
        console.error(`[API] Error: ${error.response?.status} ${error.config?.url} code=${error.response?.data?.code || "-"}`);
        const status = error.response?.status;
        const data = error.response?.data;

        // 네트워크 오류 (응답 없음) - 토스트로 알림
        if (!error.response) {
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                // 타임아웃
                toast.error(t('error.timeout', '요청 시간이 초과되었습니다'), {
                    description: t('error.timeoutDescription', '네트워크 연결을 확인하고 다시 시도해주세요')
                });
            } else {
                // 네트워크 오류 (서버 연결 불가)
                toast.error(t('error.networkError', '네트워크 오류가 발생했습니다'), {
                    description: t('error.networkErrorDescription', '인터넷 연결을 확인해주세요')
                });
            }

            const errorInfo = {
                status: 0,
                title: t('error.networkError', '네트워크 오류'),
                detail: error.message || t('error.networkErrorDetail', '서버에 연결할 수 없습니다'),
                code: error.code || 'NETWORK_ERROR',
                type: 'network_error',
                original: error
            };
            return Promise.reject(errorInfo);
        }

        // 서버 오류 (500번대) - 토스트로 알림
        if (status >= 500) {
            toast.error(t('error.serverError', '서버 오류가 발생했습니다'), {
                description: t('error.serverErrorDescription', '잠시 후 다시 시도해주세요')
            });
        }

        // 409 Conflict (2FA 필요 등) - redirectUrl이 있으면 리다이렉트
        if (status === 409 && data?.redirectUrl) {
            // IdentityServer 엔드포인트는 서버 요청이 필요
            if (data.redirectUrl.startsWith('http') ||
                data.redirectUrl.startsWith('/connect/') ||
                data.redirectUrl.startsWith('/.well-known/')) {
                window.location.href = data.redirectUrl;
            } else if (routerRef) {
                routerRef.push(data.redirectUrl);
            }
            return Promise.reject(error);
        }

        // 401 Unauthorized
        if (status === 401) {
            const currentPath = window.location.pathname;

            // 로그인 페이지가 아닌 경우에만 세션 만료 처리
            // (ASP.NET 라우팅은 /Account/Login 형태이므로 대소문자 무시 비교)
            if (!currentPath.toLowerCase().startsWith('/account/login')) {
                toast.warning(t('error.sessionExpired', '세션이 만료되었습니다'), {
                    description: t('error.sessionExpiredDescription', '다시 로그인해주세요')
                });

                if (authStoreRef) {
                    authStoreRef.logout();
                }

                if (routerRef) {
                    routerRef.push(`/account/login?ReturnUrl=${encodeURIComponent(currentPath + window.location.search)}`);
                }
            }

            // 401도 정규화된 errorInfo로 반환 (LoginPage에서 detail 사용 가능)
            const errorInfo = {
                status: status,
                title: data?.title || t('error.unauthorized', '인증 실패'),
                detail: data?.detail || t('error.unauthorizedDetail', '인증에 실패했습니다.'),
                code: data?.code || 'UNAUTHORIZED',
                type: data?.type,
                retryAfterSeconds: readRetryAfter(error),
                challengeRequired: data?.challengeRequired === true,
                original: error
            };
            return Promise.reject(errorInfo);
        }

        // RFC 7807 Problem Details 형식으로 에러 정보 추출
        const errorInfo = {
            status: status,
            title: data?.title || t('error.error', '오류'),
            detail: data?.detail || t('error.errorOccurred', '오류가 발생했습니다'),
            code: data?.code || 'UNKNOWN_ERROR',
            type: data?.type,
            retryAfterSeconds: readRetryAfter(error),
            challengeRequired: data?.challengeRequired === true,
            original: error
        };

        return Promise.reject(errorInfo);
    }
);

export default api;
